import CloseRoundedIcon from '@mui/icons-material/CloseRounded'
import EventRepeatRoundedIcon from '@mui/icons-material/EventRepeatRounded'
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded'
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Divider,
  Drawer,
  FormControlLabel,
  IconButton,
  Paper,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material'
import { alpha } from '@mui/material/styles'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import { useEffect, useMemo, useState } from 'react'
import { toast } from 'react-toastify'
import { displayNamesOfEventTypes } from 'src/consumer/constants'
import {
  getDataMapFromCommonResponse,
  isCommonResponseSuccessfulV2,
} from 'src/mentor/helpers/utilityFunctions'
import {
  buildSupportBookedEventActionErrorMessage,
  fetchSupportBookedEventDaySlots,
  rescheduleSupportBookedEvent,
} from 'src/supportPortal/services/supportBookedEventRescheduleService'

dayjs.extend(utc)

const TIME_PERIODS = [
  { id: 'earlyMorning', title: 'Early Morning', matches: (hour) => hour >= 0 && hour < 7 },
  { id: 'morning', title: 'Morning', matches: (hour) => hour >= 7 && hour < 12 },
  { id: 'afternoon', title: 'Afternoon', matches: (hour) => hour >= 12 && hour < 17 },
  { id: 'evening', title: 'Evening', matches: (hour) => hour >= 17 && hour < 22 },
  { id: 'night', title: 'Night', matches: (hour) => hour >= 22 },
]

function resolveEventDurationMinutes(bookedEvent) {
  const eventDurationMinutes = Number(bookedEvent?.durationInMins || bookedEvent?.duration)
  if (Number.isFinite(eventDurationMinutes) && eventDurationMinutes > 0) {
    return eventDurationMinutes
  }

  const eventDurationSeconds = Number(bookedEvent?.eventDuration)
  if (Number.isFinite(eventDurationSeconds) && eventDurationSeconds > 0) {
    return Math.max(15, Math.round(eventDurationSeconds / 60))
  }

  return 30
}

function resolveSlotIntervalMinutes(durationInMinutes) {
  if (durationInMinutes % 30 === 0) {
    return 30
  }
  return 15
}

function normalizeStatus(value) {
  return `${value || ''}`.trim().toLowerCase()
}

function isBookedConflict(conflict) {
  const status = normalizeStatus(conflict?.status)
  const paymentStatus = normalizeStatus(conflict?.paymentStatus)
  return status === 'booked' || paymentStatus === 'paid'
}

function isBlockedConflict(conflict) {
  const status = normalizeStatus(conflict?.status)
  const paymentStatus = normalizeStatus(conflict?.paymentStatus)
  return status === 'booking-initiated' || paymentStatus === 'pending'
}

function getPatientName(conflict) {
  return (
    conflict?.patientInfo?.name ||
    conflict?.patientInfoData?.name ||
    conflict?.userName ||
    'Patient'
  )
}

function getPractitionerName(bookedEvent) {
  return bookedEvent?.mentorName || bookedEvent?.practitionerName || 'Practitioner'
}

function SlotCard({ slot, selected, onClick }) {
  const paletteByState = {
    available: {
      border: '#cbd5e1',
      background: '#ffffff',
      color: '#0f172a',
      subText: '#64748b',
      hover: '#eff6ff',
    },
    blocked: {
      border: '#f59e0b',
      background: alpha('#f59e0b', 0.12),
      color: '#92400e',
      subText: '#b45309',
      hover: alpha('#f59e0b', 0.18),
    },
    booked: {
      border: '#cbd5e1',
      background: '#f8fafc',
      color: '#94a3b8',
      subText: '#94a3b8',
      hover: '#f8fafc',
    },
    current: {
      border: '#8b5cf6',
      background: alpha('#8b5cf6', 0.08),
      color: '#6d28d9',
      subText: '#7c3aed',
      hover: alpha('#8b5cf6', 0.12),
    },
  }

  const palette = paletteByState[slot.state] || paletteByState.available
  const disabled = slot.state === 'booked' || slot.state === 'current'

  return (
    <Button
      variant="outlined"
      fullWidth
      onClick={() => !disabled && onClick(slot)}
      disabled={disabled}
      sx={{
        minHeight: 74,
        alignItems: 'flex-start',
        justifyContent: 'flex-start',
        textTransform: 'none',
        borderRadius: 2,
        borderColor: selected ? '#2563eb' : palette.border,
        backgroundColor: selected ? alpha('#2563eb', 0.08) : palette.background,
        color: selected ? '#1d4ed8' : palette.color,
        px: 1.25,
        py: 1,
        boxShadow: selected ? '0 8px 20px rgba(37, 99, 235, 0.12)' : 'none',
        '&:hover': {
          borderColor: disabled ? palette.border : '#2563eb',
          backgroundColor: disabled ? palette.background : palette.hover,
        },
        '&.Mui-disabled': {
          borderColor: palette.border,
          color: palette.color,
          backgroundColor: palette.background,
        },
      }}
    >
      <Stack spacing={0.35} sx={{ width: '100%', alignItems: 'flex-start' }}>
        <Typography sx={{ fontSize: 13, fontWeight: 700, lineHeight: 1.1, color: 'inherit' }}>
          {slot.label}
        </Typography>
        <Typography sx={{ fontSize: 11, color: selected ? '#1d4ed8' : palette.subText }}>
          {slot.stateLabel}
        </Typography>
      </Stack>
    </Button>
  )
}

export default function SupportBookedEventRescheduleDrawer({
  open,
  onClose,
  bookedEvent,
  onSuccess,
}) {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))

  const [selectedDate, setSelectedDate] = useState(dayjs())
  const [dayBookedSlots, setDayBookedSlots] = useState([])
  const [loadingSlots, setLoadingSlots] = useState(false)
  const [selectedSlot, setSelectedSlot] = useState(null)
  const [saving, setSaving] = useState(false)
  const [submitError, setSubmitError] = useState('')

  const [sendCommunicationToClient, setSendCommunicationToClient] = useState(true)
  const [sendCommunicationToMentor, setSendCommunicationToMentor] = useState(true)
  const [handlePaymentLinks, setHandlePaymentLinks] = useState(true)
  const [sendCommunicationToBlockedClients, setSendCommunicationToBlockedClients] = useState(true)
  const [sendCommunicationToBlockedMentors, setSendCommunicationToBlockedMentors] = useState(true)
  const [confirmBlockedOverride, setConfirmBlockedOverride] = useState(false)
  const [reason, setReason] = useState('Rescheduled by support')

  const eventDurationMinutes = useMemo(
    () => resolveEventDurationMinutes(bookedEvent),
    [bookedEvent],
  )
  const slotIntervalMinutes = useMemo(
    () => resolveSlotIntervalMinutes(eventDurationMinutes),
    [eventDurationMinutes],
  )

  useEffect(() => {
    if (!open || !bookedEvent?.startTime) {
      return
    }
    setSelectedDate(dayjs.utc(bookedEvent.startTime).local().startOf('day'))
    setSelectedSlot(null)
    setConfirmBlockedOverride(false)
    setReason('Rescheduled by support')
    setSendCommunicationToClient(true)
    setSendCommunicationToMentor(true)
    setHandlePaymentLinks(true)
    setSendCommunicationToBlockedClients(true)
    setSendCommunicationToBlockedMentors(true)
    setSubmitError('')
  }, [open, bookedEvent])

  useEffect(() => {
    if (!open || !bookedEvent?.mentorId || !selectedDate) {
      return
    }

    let cancelled = false

    async function fetchSlots() {
      setLoadingSlots(true)
      const response = await fetchSupportBookedEventDaySlots({
        mentorId: bookedEvent.mentorId,
        selectedDate,
      })

      if (cancelled) {
        return
      }

      if (response?.status === 200 && isCommonResponseSuccessfulV2(response)) {
        const data = getDataMapFromCommonResponse(response)
        const slots = data?.bookedEvents || data?.bookedSlots || data?.['booked-event-info'] || []
        setDayBookedSlots(
          Array.isArray(slots)
            ? slots.filter((slot) => normalizeStatus(slot?.status) !== 'cancelled')
            : [],
        )
      } else {
        setDayBookedSlots([])
        toast.error(response?.data?.message || 'Failed to load occupied slots')
      }

      setLoadingSlots(false)
    }

    fetchSlots()

    return () => {
      cancelled = true
    }
  }, [open, bookedEvent?.mentorId, selectedDate])

  const slotOptions = useMemo(() => {
    if (!selectedDate) {
      return []
    }

    const currentStart = bookedEvent?.startTime ? dayjs.utc(bookedEvent.startTime).local() : null
    const startOfDay = selectedDate.startOf('day')
    const slots = []
    const totalMinutes = 24 * 60
    const latestStartMinute = Math.max(0, totalMinutes - eventDurationMinutes)

    for (let minuteIndex = 0; minuteIndex <= latestStartMinute; minuteIndex += slotIntervalMinutes) {
      const start = startOfDay.add(minuteIndex, 'minute')
      const end = start.add(eventDurationMinutes, 'minute')
      const isCurrentSlot =
        currentStart &&
        start.isSame(currentStart, 'minute') &&
        selectedDate.isSame(currentStart, 'day')

      const overlappingConflicts = (dayBookedSlots || []).filter((conflict) => {
        if (!conflict?.startTime || !conflict?.endTime) {
          return false
        }
        if (conflict?.id === bookedEvent?.id) {
          return false
        }
        const conflictStart = dayjs.utc(conflict.startTime).local()
        const conflictEnd = dayjs.utc(conflict.endTime).local()
        return start.isBefore(conflictEnd) && end.isAfter(conflictStart)
      })

      const bookedConflicts = overlappingConflicts.filter(isBookedConflict)
      const blockedConflicts = overlappingConflicts.filter(
        (conflict) => !bookedConflicts.includes(conflict) && isBlockedConflict(conflict),
      )

      let state = 'available'
      let stateLabel = 'Available'
      if (isCurrentSlot) {
        state = 'current'
        stateLabel = 'Current slot'
      } else if (bookedConflicts.length > 0) {
        state = 'booked'
        stateLabel = bookedConflicts.length > 1 ? 'Booked' : 'Booked'
      } else if (blockedConflicts.length > 0) {
        state = 'blocked'
        stateLabel = blockedConflicts.length > 1 ? 'Blocked (override)' : 'Blocked (override)'
      }

      const period =
        TIME_PERIODS.find((item) => item.matches(start.hour()))?.id || TIME_PERIODS[TIME_PERIODS.length - 1].id

      slots.push({
        id: start.format('YYYY-MM-DD-HH-mm'),
        start,
        end,
        label: start.format('h:mm A'),
        state,
        stateLabel,
        period,
        bookedConflicts,
        blockedConflicts,
      })
    }

    return slots
  }, [
    bookedEvent?.id,
    bookedEvent?.startTime,
    dayBookedSlots,
    eventDurationMinutes,
    selectedDate,
    slotIntervalMinutes,
  ])

  const slotGroups = useMemo(() => {
    return TIME_PERIODS.map((period) => ({
      ...period,
      slots: slotOptions.filter((slot) => slot.period === period.id),
    })).filter((period) => period.slots.length > 0)
  }, [slotOptions])

  const selectedSlotBlockedConflicts = selectedSlot?.blockedConflicts || []
  const selectedSlotBookedConflicts = selectedSlot?.bookedConflicts || []
  const selectedSlotIsBlocked = selectedSlot?.state === 'blocked'
  const currentEventTypeLabel =
    displayNamesOfEventTypes?.[bookedEvent?.eventType] || bookedEvent?.eventType || 'Session'

  const canSubmit = Boolean(
    selectedSlot &&
      selectedSlot.state !== 'booked' &&
      selectedSlot.state !== 'current' &&
      (!selectedSlotIsBlocked || confirmBlockedOverride),
  )

  async function handleSubmit() {
    if (!selectedSlot) {
      setSubmitError('')
      toast.error('Please select a new time slot')
      return
    }

    if (selectedSlot.state === 'booked' || selectedSlot.state === 'current') {
      return
    }

    if (selectedSlotIsBlocked && !confirmBlockedOverride) {
      setSubmitError('')
      toast.error('Please confirm the blocked-slot override')
      return
    }

    setSubmitError('')
    setSaving(true)
    const response = await rescheduleSupportBookedEvent({
      bookedEventId: bookedEvent.id,
      newStartTime: selectedSlot.start.utc().toISOString(),
      sendCommunicationToClient,
      sendCommunicationToMentor,
      handlePaymentLinks,
      reason: reason?.trim() || 'Rescheduled by support',
      overrideBlockedSlots: selectedSlotIsBlocked,
      blockedConflictBookedEventIds: selectedSlotBlockedConflicts.map((conflict) => conflict.id),
      sendCommunicationToBlockedClients,
      sendCommunicationToBlockedMentors,
      blockedSlotCancellationReason: selectedSlotIsBlocked
        ? `Cancelled blocked hold while rescheduling ${getPatientName(bookedEvent)} to ${selectedSlot.start.format('DD MMM, h:mm A')}`
        : undefined,
    })
    setSaving(false)

    if (response?.status === 200 && isCommonResponseSuccessfulV2(response)) {
      toast.success(
        selectedSlotIsBlocked
          ? 'Booking rescheduled and blocked slot cleared'
          : 'Booking rescheduled successfully',
      )
      onSuccess?.(getDataMapFromCommonResponse(response)?.['booked-event-info'])
      onClose?.()
      return
    }

    const errorMessage = buildSupportBookedEventActionErrorMessage(
      response,
      'Failed to reschedule booking',
    )
    setSubmitError(errorMessage)
    toast.error(errorMessage)
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Drawer
        anchor={isMobile ? 'bottom' : 'right'}
        open={open}
        onClose={onClose}
        ModalProps={{ keepMounted: true }}
        PaperProps={{
          sx: {
            width: isMobile ? '100%' : 540,
            maxWidth: '100%',
            height: isMobile ? '92vh' : '100vh',
            borderTopLeftRadius: isMobile ? 20 : 0,
            borderTopRightRadius: isMobile ? 20 : 0,
            overflow: 'hidden',
          },
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
          <Box
            sx={{
              px: 2.5,
              py: 2,
              borderBottom: '1px solid rgba(148, 163, 184, 0.2)',
              background: 'linear-gradient(180deg, rgba(79, 70, 229, 0.06) 0%, rgba(255, 255, 255, 0.98) 100%)',
            }}
          >
            <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={2}>
              <Box>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.75 }}>
                  <EventRepeatRoundedIcon sx={{ color: '#4f46e5', fontSize: 22 }} />
                  <Typography sx={{ fontSize: 19, fontWeight: 800, color: '#0f172a' }}>
                    Reschedule Booking
                  </Typography>
                </Stack>
                <Typography sx={{ color: '#475569', fontSize: 13 }}>
                  {getPatientName(bookedEvent)} with {getPractitionerName(bookedEvent)}
                </Typography>
                <Typography sx={{ color: '#64748b', fontSize: 12, mt: 0.4 }}>
                  {currentEventTypeLabel} · Current time {dayjs.utc(bookedEvent?.startTime).local().format('DD MMM, h:mm A')}
                </Typography>
              </Box>
              <IconButton onClick={onClose}>
                <CloseRoundedIcon />
              </IconButton>
            </Stack>
          </Box>

          <Box sx={{ flex: 1, overflowY: 'auto', px: 2.5, py: 2.5 }}>
            <Stack spacing={2}>
              {submitError ? (
                <Alert severity="error" sx={{ borderRadius: 2 }}>
                  {submitError}
                </Alert>
              ) : null}
              <Paper
                variant="outlined"
                sx={{
                  p: 2,
                  borderRadius: 3,
                  borderColor: 'rgba(148, 163, 184, 0.28)',
                  backgroundColor: 'rgba(248, 250, 252, 0.88)',
                }}
              >
                <Stack spacing={1.5}>
                  <DatePicker
                    label="Choose a date"
                    value={selectedDate}
                    onChange={(nextDate) => {
                      if (!nextDate) {
                        return
                      }
                      setSelectedDate(nextDate.startOf('day'))
                      setSelectedSlot(null)
                      setConfirmBlockedOverride(false)
                      setSubmitError('')
                    }}
                    disablePast
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        size: 'small',
                      },
                    }}
                  />
                  <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', rowGap: 1 }}>
                    <Chip size="small" label="Available" sx={{ backgroundColor: '#eff6ff', color: '#1d4ed8' }} />
                    <Chip size="small" label="Blocked (override)" sx={{ backgroundColor: alpha('#f59e0b', 0.16), color: '#b45309' }} />
                    <Chip size="small" label="Booked" sx={{ backgroundColor: '#f1f5f9', color: '#64748b' }} />
                    <Chip size="small" label="Current slot" sx={{ backgroundColor: alpha('#8b5cf6', 0.14), color: '#6d28d9' }} />
                  </Stack>
                </Stack>
              </Paper>

              {loadingSlots ? (
                <Paper
                  variant="outlined"
                  sx={{
                    p: 3,
                    borderRadius: 3,
                    borderColor: 'rgba(148, 163, 184, 0.24)',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: 1.5,
                  }}
                >
                  <CircularProgress size={30} />
                  <Typography sx={{ color: '#475569', fontSize: 13 }}>
                    Loading booked and blocked slots...
                  </Typography>
                </Paper>
              ) : (
                <Paper
                  variant="outlined"
                  sx={{
                    p: 2,
                    borderRadius: 3,
                    borderColor: 'rgba(148, 163, 184, 0.24)',
                  }}
                >
                  <Typography sx={{ fontWeight: 700, color: '#0f172a', mb: 0.5 }}>
                    Choose a new time
                  </Typography>
                  <Typography sx={{ color: '#64748b', fontSize: 12.5, mb: 2 }}>
                    Booked slots are locked. Blocked slots can be overridden after confirmation.
                  </Typography>

                  <Stack spacing={2.25}>
                    {slotGroups.map((group) => (
                      <Box key={group.id}>
                        <Typography sx={{ fontSize: 12, fontWeight: 800, color: '#475569', mb: 1 }}>
                          {group.title}
                        </Typography>
                        <Box
                          sx={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(auto-fill, minmax(110px, 1fr))',
                            gap: 1,
                          }}
                        >
                          {group.slots.map((slot) => (
                            <SlotCard
                              key={slot.id}
                              slot={slot}
                              selected={selectedSlot?.id === slot.id}
                              onClick={(nextSlot) => {
                                setSelectedSlot(nextSlot)
                                setConfirmBlockedOverride(false)
                                setSubmitError('')
                              }}
                            />
                          ))}
                        </Box>
                      </Box>
                    ))}
                  </Stack>
                </Paper>
              )}

              <Paper
                variant="outlined"
                sx={{
                  p: 2,
                  borderRadius: 3,
                  borderColor: selectedSlot ? 'rgba(37, 99, 235, 0.3)' : 'rgba(148, 163, 184, 0.24)',
                  backgroundColor: selectedSlot ? alpha('#eff6ff', 0.6) : '#ffffff',
                }}
              >
                {selectedSlot ? (
                  <Stack spacing={1.5}>
                    <Box>
                      <Typography sx={{ fontWeight: 800, color: '#0f172a' }}>
                        {selectedSlot.start.format('dddd, DD MMM')} · {selectedSlot.label}
                      </Typography>
                      <Typography sx={{ color: '#475569', fontSize: 12.5 }}>
                        Duration {eventDurationMinutes} mins
                      </Typography>
                    </Box>

                    {selectedSlotBookedConflicts.length > 0 && (
                      <Alert severity="error">
                        This slot is already booked and cannot be used.
                      </Alert>
                    )}

                    {selectedSlotIsBlocked && (
                      <Alert
                        severity="warning"
                        icon={<WarningAmberRoundedIcon fontSize="inherit" />}
                        sx={{ '& .MuiAlert-message': { width: '100%' } }}
                      >
                        <Typography sx={{ fontWeight: 700, mb: 0.75 }}>
                          This time overlaps with blocked session{selectedSlotBlockedConflicts.length > 1 ? 's' : ''}
                        </Typography>
                        <Stack spacing={0.75}>
                          {selectedSlotBlockedConflicts.map((conflict) => (
                            <Box
                              key={conflict.id}
                              sx={{
                                p: 1,
                                borderRadius: 2,
                                backgroundColor: alpha('#f59e0b', 0.12),
                              }}
                            >
                              <Typography sx={{ fontSize: 12.5, fontWeight: 700 }}>
                                {getPatientName(conflict)}
                              </Typography>
                              <Typography sx={{ fontSize: 12 }}>
                                {dayjs.utc(conflict.startTime).local().format('DD MMM, h:mm A')} -{' '}
                                {dayjs.utc(conflict.endTime).local().format('h:mm A')}
                              </Typography>
                            </Box>
                          ))}
                        </Stack>
                      </Alert>
                    )}

                    <Divider />

                    <TextField
                      label="Reschedule note"
                      size="small"
                      fullWidth
                      multiline
                      minRows={2}
                      value={reason}
                      onChange={(event) => setReason(event.target.value)}
                    />

                    <Stack spacing={0.5}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={sendCommunicationToClient}
                            onChange={(event) => setSendCommunicationToClient(event.target.checked)}
                          />
                        }
                        label="Notify patient about the reschedule"
                      />
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={sendCommunicationToMentor}
                            onChange={(event) => setSendCommunicationToMentor(event.target.checked)}
                          />
                        }
                        label="Notify practitioner about the reschedule"
                      />
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={handlePaymentLinks}
                            onChange={(event) => setHandlePaymentLinks(event.target.checked)}
                          />
                        }
                        label="Handle payment links for this reschedule"
                      />
                    </Stack>

                    {selectedSlotIsBlocked && (
                      <>
                        <Divider />
                        <Typography sx={{ fontWeight: 700, color: '#92400e', fontSize: 13 }}>
                          Blocked-slot override
                        </Typography>
                        <Stack spacing={0.5}>
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={sendCommunicationToBlockedClients}
                                onChange={(event) => setSendCommunicationToBlockedClients(event.target.checked)}
                              />
                            }
                            label="Notify blocked-session patient(s) when their hold is cancelled"
                          />
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={sendCommunicationToBlockedMentors}
                                onChange={(event) => setSendCommunicationToBlockedMentors(event.target.checked)}
                              />
                            }
                            label="Notify blocked-session practitioner(s) when their hold is cancelled"
                          />
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={confirmBlockedOverride}
                                onChange={(event) => setConfirmBlockedOverride(event.target.checked)}
                              />
                            }
                            label="I understand that these blocked session holds will be cancelled and this booking will take the slot"
                          />
                        </Stack>
                      </>
                    )}
                  </Stack>
                ) : (
                  <Typography sx={{ color: '#64748b', fontSize: 13 }}>
                    Select a time slot to review the reschedule details and communication options.
                  </Typography>
                )}
              </Paper>
            </Stack>
          </Box>

          <Box
            sx={{
              px: 2.5,
              py: 2,
              borderTop: '1px solid rgba(148, 163, 184, 0.2)',
              backgroundColor: '#ffffff',
            }}
          >
            <Stack direction="row" spacing={1.5} justifyContent="flex-end">
              <Button onClick={onClose} color="inherit">
                Close
              </Button>
              <Button
                variant="contained"
                onClick={handleSubmit}
                disabled={!canSubmit || saving}
                sx={{ minWidth: 180 }}
              >
                {saving
                  ? 'Rescheduling...'
                  : selectedSlotIsBlocked
                    ? 'Override & Reschedule'
                    : 'Reschedule Booking'}
              </Button>
            </Stack>
          </Box>
        </Box>
      </Drawer>
    </LocalizationProvider>
  )
}
